import { useEffect, useMemo, useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useFetchService } from '../../../utils/fetchService';
import { httpRequest } from '../../../utils/network.service';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { CountryPickerModal } from './CountryPickerModal';
import { CountryCatalogEntry, createCountryOptions, normalizeCountryName } from './countrySettings';
import { OrganizationSettingsDialog, OrganizationSettingsSnackbarDismissButton } from './OrganizationSettingsDialog';
import { RiskyNationsPanel } from './RiskyNationsPanel';
import { formatRiskyNationDate, getConfirmationCopy, isHomeCountryConflict, sortRiskyNations } from './RiskyNations.utils';
import {
  ConfirmationAction,
  HighRiskCountriesResponse,
  HighRiskCountry,
  HomeCountryConflict,
  HomeCountryConflictModal,
  SortDirection,
  SortField,
} from './RiskyNations.types';

interface RiskyNationsCardProps {
  isAdmin: boolean;
  homeCountryCode?: string;
}

const REEVALUATION_MESSAGE = 'Please allow up to 2 hours to re-process all app analysis and enabled Policies.';
const HIGH_RISK_COUNTRIES_PATH = '/list/high-risk-countries';

const getDisabledReason = (countryCode: string, homeCountryCode: string | undefined, configuredCountryCodes: Set<string>) => {
  if (countryCode === homeCountryCode) return 'Home country';
  if (configuredCountryCodes.has(countryCode)) return 'On risky nations list';
  return undefined;
};

const getErrorMessage = (data: unknown, fallbackMessage: string) => {
  if (!data || typeof data !== 'object' || !('message' in data)) return fallbackMessage;

  const { message } = data as { message?: unknown };
  return typeof message === 'string' ? message : fallbackMessage;
};

const getMutationRequest = (action: ConfirmationAction): { path: string; method: 'POST' | 'DELETE'; body?: unknown } => {
  const basePath = `${process.env.LIST_SERVICE_URL}${HIGH_RISK_COUNTRIES_PATH}`;

  if (action.kind === 'remove') {
    return { path: `${basePath}/${encodeURIComponent(action.country.countryCode)}`, method: 'DELETE' };
  }

  if (action.kind === 'removeMultiple') {
    return {
      path: `${basePath}/batch`,
      method: 'DELETE',
      body: { countryCodes: action.countries.map(country => country.countryCode) },
    };
  }

  if (action.kind === 'restore') {
    return { path: `${basePath}/restore-defaults`, method: 'POST' };
  }

  return { path: basePath, method: 'DELETE' };
};

const RiskyNationsSuccessToast = ({ title }: { title: string }) => (
  <ToastContent>
    <ToastTitle>{title}</ToastTitle>
    <ToastDescription>{REEVALUATION_MESSAGE}</ToastDescription>
  </ToastContent>
);

const renderToastDismissAction = (onDismiss: (id: SnackbarKey) => void, snackbarId: SnackbarKey) => (
  <OrganizationSettingsSnackbarDismissButton aria-label="Dismiss notification" size="small" onClick={() => onDismiss(snackbarId)}>
    <Close fontSize="small" />
  </OrganizationSettingsSnackbarDismissButton>
);

export const RiskyNationsCard = ({ isAdmin, homeCountryCode }: RiskyNationsCardProps) => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    data: riskyNationsData,
    error: riskyNationsError,
    mutate: mutateRiskyNations,
  } = useFetchService<HighRiskCountriesResponse>(HIGH_RISK_COUNTRIES_PATH, accessToken);
  const { data: countriesData, error: countriesError } = useFetchService<CountryCatalogEntry[]>('/list/countryCodes', accessToken);

  const [sortField, setSortField] = useState<SortField>('country');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingCountryCodes, setPendingCountryCodes] = useState<string[]>([]);
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([]);
  const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction | null>(null);
  const [homeCountryConflict, setHomeCountryConflict] = useState<HomeCountryConflictModal | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [knownDefaultState, setKnownDefaultState] = useState<boolean | undefined>(riskyNationsData?.isDefault);

  useEffect(() => {
    setKnownDefaultState(riskyNationsData?.isDefault);
  }, [riskyNationsData]);

  const riskyNations = useMemo(() => riskyNationsData?.countries || [], [riskyNationsData?.countries]);
  const configuredCountryCodes = useMemo(
    () => new Set<string>(riskyNations.map(country => country.countryCode.toUpperCase())),
    [riskyNations],
  );
  const defaultHighRiskCountryCodes = useMemo(
    () => new Set((riskyNationsData?.defaultHighRiskCountries || []).map(countryCode => countryCode.toUpperCase())),
    [riskyNationsData?.defaultHighRiskCountries],
  );
  const normalizedHomeCountryCode = homeCountryCode?.toUpperCase();
  const countryOptions = useMemo(() => createCountryOptions(countriesData || []), [countriesData]);
  const pickerOptions = useMemo(
    () =>
      countryOptions.map(option => ({
        ...option,
        disabledReason: getDisabledReason(option.value, normalizedHomeCountryCode, configuredCountryCodes),
      })),
    [configuredCountryCodes, countryOptions, normalizedHomeCountryCode],
  );
  const sortedRiskyNations = useMemo(
    () => sortRiskyNations(riskyNations, sortField, sortDirection),
    [riskyNations, sortDirection, sortField],
  );
  const { ofacCount, customCount } = useMemo(
    () =>
      riskyNations.reduce(
        (counts, country) => ({
          ofacCount: counts.ofacCount + Number(country.source === 'OFAC'),
          customCount: counts.customCount + Number(country.source === 'CUSTOM'),
        }),
        { ofacCount: 0, customCount: 0 },
      ),
    [riskyNations],
  );

  const total = riskyNationsData?.total ?? riskyNations.length;
  const isLoading = !riskyNationsData && !riskyNationsError;
  const hasError = Boolean(riskyNationsError);
  const canAddCountry = Boolean(riskyNationsData && countriesData && !riskyNationsError && !countriesError);
  const latestUpdatedDate = formatRiskyNationDate(riskyNationsData?.lastUpdated);

  useEffect(() => {
    setSelectedCountryCodes(currentCodes => {
      const configuredCodes = currentCodes.filter(countryCode => configuredCountryCodes.has(countryCode));
      return configuredCodes.length === currentCodes.length ? currentCodes : configuredCodes;
    });
  }, [configuredCountryCodes]);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(currentDirection => (currentDirection === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }

    setSortField(field);
    setSortDirection('ASC');
  };

  const closeAddModal = () => {
    if (isSaving) return;

    setIsAddModalOpen(false);
    setPendingCountryCodes([]);
  };

  const openAddModal = () => {
    setPendingCountryCodes([]);
    setIsAddModalOpen(true);
  };

  const toggleCountrySelection = (countryCode: string) => {
    setSelectedCountryCodes(currentCodes =>
      currentCodes.includes(countryCode) ? currentCodes.filter(currentCode => currentCode !== countryCode) : [...currentCodes, countryCode],
    );
  };

  const toggleAllCountrySelection = (selected: boolean) => {
    setSelectedCountryCodes(selected ? riskyNations.map(country => country.countryCode) : []);
  };

  const openRemoveSelected = () => {
    const selectedCountries = selectedCountryCodes
      .map(countryCode => riskyNations.find(country => country.countryCode === countryCode))
      .filter(Boolean);
    if (selectedCountries.length) setConfirmationAction({ kind: 'removeMultiple', countries: selectedCountries });
  };

  const openRemoveCountry = (country: HighRiskCountry) => {
    if (selectedCountryCodes.length > 1 && selectedCountryCodes.includes(country.countryCode)) {
      openRemoveSelected();
      return;
    }

    setConfirmationAction({ kind: 'remove', country });
  };

  const showConflict = (conflict: HomeCountryConflict, operation: HomeCountryConflictModal['operation']) => {
    setHomeCountryConflict({
      homeCountryName: conflict.homeCountryName || conflict.homeCountryCode,
      message: conflict.message,
      operation,
    });
  };

  const openRestoreDefaults = () => {
    if (normalizedHomeCountryCode && defaultHighRiskCountryCodes.has(normalizedHomeCountryCode)) {
      const homeCountry = countryOptions.find(country => country.value === normalizedHomeCountryCode);
      setHomeCountryConflict({
        homeCountryName: homeCountry?.label || normalizedHomeCountryCode,
        message: '',
        operation: 'RESTORE_DEFAULTS',
      });
      return;
    }

    setConfirmationAction({ kind: 'restore' });
  };

  const showSuccessToast = (title: string) => {
    enqueueSnackbar(<RiskyNationsSuccessToast title={title} />, {
      variant: 'success',
      action: renderToastDismissAction.bind(null, closeSnackbar),
    });
  };

  const showRequestError = (data: unknown, fallbackMessage: string) => {
    enqueueSnackbar(getErrorMessage(data, fallbackMessage), { variant: 'error' });
  };

  const saveResponse = async (response: HighRiskCountriesResponse, defaultState: boolean) => {
    const isDefault = response.isDefault ?? defaultState;
    setKnownDefaultState(isDefault);
    await mutateRiskyNations(
      {
        ...response,
        defaultHighRiskCountries: response.defaultHighRiskCountries ?? riskyNationsData?.defaultHighRiskCountries,
        isDefault,
      },
      false,
    );
  };

  const addCountries = async () => {
    if (!pendingCountryCodes.length || isSaving) return;

    const selectedCountries = pendingCountryCodes
      .map(countryCode => countryOptions.find(country => country.value === countryCode))
      .filter(Boolean);
    if (selectedCountries.length !== pendingCountryCodes.length) return;

    setIsSaving(true);
    const result = await httpRequest({
      path: `${process.env.LIST_SERVICE_URL}${HIGH_RISK_COUNTRIES_PATH}`,
      method: 'POST',
      body: { countryCodes: pendingCountryCodes },
      accessToken,
    });
    setIsSaving(false);

    if (result.error) {
      if (result.response?.status === 409 && isHomeCountryConflict(result.data)) {
        setIsAddModalOpen(false);
        setPendingCountryCodes([]);
        showConflict(result.data, 'ADD');
        return;
      }

      showRequestError(result.data, 'Error adding risky nation.');
      return;
    }

    await saveResponse(result.data as HighRiskCountriesResponse, false);
    closeAddModal();
    const toastTitle =
      selectedCountries.length === 1
        ? `${selectedCountries[0].label} added to risky nations`
        : `${selectedCountries.length} countries added to risky nations`;
    showSuccessToast(toastTitle);
  };

  const confirmMutation = async () => {
    if (!confirmationAction || isSaving) return;

    const action = confirmationAction;
    const request = getMutationRequest(action);

    setIsSaving(true);
    const result = await httpRequest({ ...request, accessToken });
    setIsSaving(false);

    if (result.error) {
      if (action.kind === 'restore' && result.response?.status === 409 && isHomeCountryConflict(result.data)) {
        setConfirmationAction(null);
        showConflict(result.data, 'RESTORE_DEFAULTS');
        return;
      }

      showRequestError(result.data, 'Error updating risky nations.');
      return;
    }

    await saveResponse(result.data as HighRiskCountriesResponse, action.kind === 'restore');
    setConfirmationAction(null);

    if (action.kind === 'remove') {
      setSelectedCountryCodes(currentCodes => currentCodes.filter(countryCode => countryCode !== action.country.countryCode));
      showSuccessToast(`${normalizeCountryName(action.country.name)} removed from risky nations`);
    }
    if (action.kind === 'removeMultiple') {
      setSelectedCountryCodes([]);
      const countryLabel = action.countries.length === 1 ? 'country' : 'countries';
      showSuccessToast(`${action.countries.length} ${countryLabel} removed from risky nations`);
    }
    if (action.kind === 'clear') showSuccessToast('Risky nations list cleared');
    if (action.kind === 'restore') showSuccessToast('Risky nations restored to defaults');
    if (action.kind === 'clear' || action.kind === 'restore') setSelectedCountryCodes([]);
  };

  const lastConfirmationCopy = useRef(getConfirmationCopy(null));
  if (confirmationAction) lastConfirmationCopy.current = getConfirmationCopy(confirmationAction);
  const confirmationCopy = lastConfirmationCopy.current;
  const conflictTitle = homeCountryConflict?.operation === 'ADD' ? 'Cannot add country' : 'Cannot restore defaults';
  const conflictBody =
    homeCountryConflict?.operation === 'ADD'
      ? homeCountryConflict.message ||
        `${homeCountryConflict.homeCountryName} cannot be added to Risky Nations because it is the organization's Home Country.`
      : `The default Risky Nations list cannot be restored because your Home Country is set to ${
          homeCountryConflict?.homeCountryName || ''
        }, a current default Risky Nation per OFAC. Update your Home Country or continue building a custom Risky Nations list.`;

  return (
    <>
      <RiskyNationsPanel
        countries={sortedRiskyNations}
        total={total}
        ofacCount={ofacCount}
        customCount={customCount}
        latestUpdatedDate={latestUpdatedDate}
        isAdmin={isAdmin}
        isLoading={isLoading}
        hasError={hasError}
        isSaving={isSaving}
        canAddCountry={canAddCountry}
        restoreDefaultsDisabled={knownDefaultState === true}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={toggleSort}
        onAddCountry={openAddModal}
        onClear={() => setConfirmationAction({ kind: 'clear' })}
        onRestore={openRestoreDefaults}
        onRemove={openRemoveCountry}
        selectedCountryCodes={selectedCountryCodes}
        onToggleCountrySelection={toggleCountrySelection}
        onToggleAllCountrySelection={toggleAllCountrySelection}
        onRemoveSelected={openRemoveSelected}
      />

      <CountryPickerModal
        id="add-risky-nations"
        title="Add risky nations"
        open={isAddModalOpen}
        options={pickerOptions}
        multiple
        selectedValues={pendingCountryCodes}
        onSelectionChange={setPendingCountryCodes}
        onClose={closeAddModal}
        onSave={addCountries}
        isSaving={isSaving}
        saveDisabled={!pendingCountryCodes.length || isSaving}
      />

      <OrganizationSettingsDialog
        id="risky-nations-confirmation"
        open={Boolean(confirmationAction)}
        title={confirmationCopy.title}
        body={confirmationCopy.body}
        primaryLabel={confirmationCopy.confirmLabel}
        onPrimary={confirmMutation}
        onClose={() => {
          if (!isSaving) setConfirmationAction(null);
        }}
        showCancel
        isSaving={isSaving}
      />

      <OrganizationSettingsDialog
        id="home-country-conflict"
        open={Boolean(homeCountryConflict)}
        title={conflictTitle}
        body={conflictBody}
        primaryLabel="OK"
        onPrimary={() => setHomeCountryConflict(null)}
        onClose={() => setHomeCountryConflict(null)}
      />
    </>
  );
};

const ToastContent = styled('div')`
  color: #ffffff;
`;

const ToastTitle = styled('div')`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
`;

const ToastDescription = styled('div')`
  margin-top: 3px;
  font-size: 12px;
  line-height: 18px;
`;
